import { Box, Button, Paper, Toolbar } from '@material-ui/core';
import React, { ReactElement, useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import Close from '@material-ui/icons/Close';
import Add from '@material-ui/icons/Add';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import Info from '@material-ui/icons/Info';

import {
	Project,
	Comparison,
	mapSelectorSelectMap,
	mapSelectorDeselectMap,
	mapSelectorSetSelectedList,
	mapSelectorSetSelectable,
	AppState
} from '../../../redux';
import { Body1, Header4 } from '../../atoms';
import {
	TabContainer,
	PageContainer,
	TabContentContainer,
	IndexMapSelector,
	SearchMapSelector,
	SelectedMapDialog,
	BosatlasMapSelector
} from '../../molecules';
import { IProjectPreviewDialogProps, ProjectPreviewDialog } from '..';
import { useBrandName, useIsSafeExamBrowser } from '../../../hooks';

import './map-selector.styles.scss';
import { useTranslation } from 'react-i18next';

export interface IMapSelectorOwnProps {
	project: Project;
	comparison?: Comparison;
	selectedProjects: Project[];
	onConfirm: () => void;
	onCancel: () => void;
}

export interface IMapSelectorStateProps {
	selectable: boolean;
	selectMap: (map: Project) => void;
	deselectMap: (map: Project) => void;
	setSelected: (maps: Project[]) => void;
	setSelectable: (selectable: boolean) => void;
}

function MapSelectorView(
	props: IMapSelectorStateProps & IMapSelectorOwnProps
): ReactElement {
	const {
		selectable,
		project,
		comparison,
		selectedProjects,
		selectMap,
		deselectMap,
		onConfirm,
		setSelected,
		setSelectable,
		onCancel
	} = props;
	const { t } = useTranslation(['general', 'comparison']);
	const brandName: string = useBrandName();
	const isSafeExamBrowser = useIsSafeExamBrowser();

	const [activeTab, setActiveTab] = useState<number>(0);
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [projectPreviewState, setProjectPreviewState] =
		useState<IProjectPreviewDialogProps>({
			open: false,
			project: undefined,
			onClose: onClosePreviewDialog
		});

	useEffect(() => {
		if (comparison) {
			setSelected(comparison.projects.slice(1));
		} else {
			setSelected([]);
		}
	}, []);

	useEffect(() => {
		if (selectedProjects.length === 3) setSelectable(false);
		else setSelectable(true);
	}, [selectedProjects]);

	function onMapClick(map: Project): void {
		const selected: boolean = selectedProjects.some(
			(selectedProject: Project) => {
				const isSelectedProject: boolean =
					selectedProject.id !== undefined;
				const isMapProject: boolean = map.id !== undefined;

				if (isSelectedProject && isMapProject)
					return map.id === selectedProject.id;

				return isSelectedProject
					? false
					: map.metaDataId === selectedProject.metaDataId;
			}
		);

		if (selected) {
			deselectMap(map);
		} else if (selectable) {
			selectMap(map);
		}
	}

	function onShowPreview(map: Project): void {
		setProjectPreviewState({
			...projectPreviewState,
			open: true,
			project: map
		});
	}

	function onClosePreviewDialog(): void {
		setProjectPreviewState({ ...projectPreviewState, open: false });
	}

	function handleDialogBackPressed(): void {
		setDialogOpen(false);
		onCancel();
	}

	function navigateToIndex(): void {
		setActiveTab(1);
	}

	function getSelectableTabs(): string[] {
		return isSafeExamBrowser
			? [t('comparison:tabs.search'), t('comparison:tabs.index')]
			: [
					t('comparison:tabs.search'),
					t('comparison:tabs.index'),
					t('comparison:tabs.myAtlas', { brandName })
			  ];
	}

	return (
		<Box className="map-selector">
			<Paper className="map-selector-header" elevation={0} square>
				<Box className="header-info">
					<Header4>{t('comparison:chooseCompare')}</Header4>
					<Body1>{t('comparison:searchMaps', { brandName })}</Body1>
				</Box>
				<TabContainer
					tabs={getSelectableTabs()}
					activeTab={activeTab}
					onTabChange={setActiveTab}
					centered
				/>
			</Paper>
			<Box className="map-selector-content">
				<PageContainer id="map-selector">
					<TabContentContainer activeTab={activeTab}>
						<SearchMapSelector
							projectSelection={selectedProjects}
							onMapSelect={onMapClick}
							onShowPreview={onShowPreview}
							onNavigateToIndex={navigateToIndex}
						/>
						<IndexMapSelector
							mapSelection={selectedProjects}
							onMapSelect={onMapClick}
							onShowPreview={onShowPreview}
						/>
						<BosatlasMapSelector
							disabled={!selectable}
							projectSelection={selectedProjects}
							onProjectClicked={onMapClick}
							onShowPreview={onShowPreview}
						/>
					</TabContentContainer>
				</PageContainer>
			</Box>
			{selectedProjects.length === 3 && (
				<Paper className="map-selector-warning" elevation={10}>
					<Box className="map-selector-warning-content">
						<Info />
						<Body1>{t('comparison:chooseMax4Maps')}</Body1>
					</Box>
				</Paper>
			)}
			<Toolbar className="map-selector-toolbar" />
			<Paper className="map-selector-footer" elevation={0} square>
				<Button variant="outlined" color="primary" onClick={onCancel}>
					<Close /> {t('general:dialog.cancel')}
				</Button>
				<Box
					className="map-selector-footer-selection"
					onClick={() => setDialogOpen(!dialogOpen)}
				>
					<Body1 className="selected-maps">
						{t('comparison:amountChosenMaps', {
							amount: selectedProjects.length + 1
						})}
					</Body1>
					<ArrowDropDown className="arrow" />
				</Box>
				<Button
					onClick={onConfirm}
					variant="contained"
					color="primary"
					disabled={selectedProjects.length === 0}
				>
					<Add />
					{t('comparison:addToComparison')}
				</Button>
			</Paper>
			<SelectedMapDialog
				existingComparison={comparison !== undefined}
				project={project}
				selectedProjects={selectedProjects}
				open={dialogOpen}
				deselectMap={deselectMap}
				onClose={() => setDialogOpen(false)}
				onConfirmSelection={onConfirm}
				onBackPressed={handleDialogBackPressed}
			/>
			{projectPreviewState.project && (
				<ProjectPreviewDialog {...projectPreviewState} />
			)}
		</Box>
	);
}

function mapStateToProps(state: AppState) {
	return {
		selectable: state.mapSelector.selectable
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		selectMap: (map: Project) => dispatch(mapSelectorSelectMap(map)),
		deselectMap: (map: Project) => dispatch(mapSelectorDeselectMap(map)),
		setSelected: (maps: Project[]) =>
			dispatch(mapSelectorSetSelectedList(maps)),
		setSelectable: (selectable: boolean) =>
			dispatch(mapSelectorSetSelectable(selectable))
	};
}

export const MapSelector = connect(
	mapStateToProps,
	mapDispatchToProps
)(MapSelectorView);
